import React, { Component } from 'react';
import uuid from 'uuid/v4';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import Dropdown from '../dropdown';
import ElementWithPopover from '../element-with-popover';
import DisabledOverlay from './disabled-overlay';
import ExpiredCopyNoPayments from './expired-copy.no-payments';
import ExpiredCopyWithPayments from './expired-copy.with-payments';
import ExpiryWarningWithPayments from './expiry-warning.with-payments';
import ExpiryWarningNoPayments from './expiry-warning.no-payments';
import money, { format } from '../../lib/money';
import { formatMessage } from '../../lib/intl';
import {
  Time,
  TIME_FORMAT_RELATIVE,
} from '../../../components/Time';
import Purchase from '../../lib/purchase';
import Payment from '../../lib/payment';
import '../../css/organizer-controls.css';

const i18n = defineMessages({
  cantMakePurchaseMinSlots: {
    id: 'organizerControls.cantMakePurchase.minSlots',
    defaultMessage: `This group hasn't reached {minSlots} {slotsName} yet.
      Invite more people to get there.`
  },
  cantMakePurchaseMinSlots_adult: {
    id: 'organizerControls.cantMakePurchase.minSlots_adult',
    defaultMessage: `This group hasn't reached {minSlots} adults yet.
      Invite more people to get there.`
  },
  cantMakePurchaseMinSlots_passenger: {
    id: 'organizerControls.cantMakePurchase.minSlots_passenger',
    defaultMessage: `This group hasn't reached {minSlots} passengers yet.
      Invite more people to get there.`
  },
  cantMakePurchaseMinSlots_ticket: {
    id: 'organizerControls.cantMakePurchase.minSlots_ticket',
    defaultMessage: `This group hasn't reached {minSlots} tickets yet.
      Invite more people to get there.`
  },
  cantMakePurchaseMinSlots_seat: {
    id: 'organizerControls.cantMakePurchase.minSlots_seat',
    defaultMessage: `This group hasn't reached {minSlots} seats yet.
      Invite more people to get there.`
  },
  cantMakePurchaseMinSlots_item: {
    id: 'organizerControls.cantMakePurchase.minSlots_item',
    defaultMessage: `This group hasn't reached {minSlots} items yet.
      Invite more people to get there.`
  },
  cantMakePurchaseMinSlots_shirt: {
    id: 'organizerControls.cantMakePurchase.minSlots_shirt',
    defaultMessage: `This group hasn't reached {minSlots} shirts yet.
      Invite more people to get there.`
  },
  cantMakePurchaseMinSlots_person: {
    id: 'organizerControls.cantMakePurchase.minSlots_person',
    defaultMessage: `This group hasn't reached {minSlots} people yet.
      Invite more people to get there.`
  },
  cantMakePurchaseMinSlots_guest: {
    id: 'organizerControls.cantMakePurchase.minSlots_guest',
    defaultMessage: `This group hasn't reached {minSlots} guests yet.
      Invite more people to get there.`
  },
  cantMakePurchaseMinSlots_player: {
    id: 'organizerControls.cantMakePurchase.minSlots_player',
    defaultMessage: `This group hasn't reached {minSlots} players yet.
      Invite more people to get there.`
  },
  cantMakePurchaseMinSlots_student: {
    id: 'organizerControls.cantMakePurchase.minSlots_student',
    defaultMessage: `This group hasn't reached {minSlots} students yet.
      Invite more people to get there.`
  },

  cantMakePurchaseAmount: {
    id: 'organizerControls.cantMakePurchase.amount',
    defaultMessage: `Only {committedAmount} of the total is committed so far.
      Invite more people to get there.`,
  },
  toFixYouCan: {
    id: 'organizeControls.cantMakePurchase.solution.youCan',
    defaultMessage: 'You can',
  },
  toFixCover: {
    id: 'organizeControls.cantMakePurchase.solution.cover',
    defaultMessage: 'cover the difference',
  },
  toFixToComplete: {
    id: 'organizeControls.cantMakePurchase.solution.toComplete',
    defaultMessage: 'to complete your purchase.',
  },
});

export class OrganizerControls extends Component {
  constructor(props) {
    super(props);

    this.formatMessage = formatMessage(this.props.intl);

    this.onMakeChanges = this.onMakeChanges.bind(this);
    this.onInvite = this.onInvite.bind(this);
    this.onCoverDifference = this.onCoverDifference.bind(this);
  }

  onMakeChanges() {
    this.props.onMakeChanges();
  }

  onInvite() {
    this.props.onInvite();
  }

  onCoverDifference() {
    this.props.onCoverDifference();
  }

  get committedAmount() { return money(this.props.committedAmount); }

  canMakePurchase() {
    return this.props.canMakePurchase;
  }

  showDifferencePopover() {
    const { purchase } = this.props;

    if (purchase.tipped) {
      return false;
    }

    if (purchase.currentPayment && !Payment.isActive(purchase.currentPayment.status)) {
      return false;
    }

    return true;
  }

  cantMakePurchaseCopy() {
    const { minSlots, purchase } = this.props;
    const components = [];

    if (minSlots) {
      components.push(
        <p key={uuid()}>
          {this.formatMessage(i18n.cantMakePurchaseMinSlots, { minSlots }, purchase.slotName)}
        </p>,
      );
    }

    if (this.committedAmount) {
      components.push(
        <p key={uuid()}>
          {
            this.formatMessage(i18n.cantMakePurchaseAmount, {
              committedAmount: format(this.committedAmount),
            })
          }
        </p>,
      );
    }

    components.push(
      <p className="cool-grey" key={uuid()}>
        {this.formatMessage(i18n.toFixYouCan) + ' '}
        <a className="contribute-link" href="#cover">
          {this.formatMessage(i18n.toFixCover)}
        </a>
        {' ' + this.formatMessage(i18n.toFixToComplete)}
      </p>,
    );

    return <div>{components}</div>;
  }

  expirationDateCopy() {
    const { merchant, nextPaymentDueDeadline } = this.props.purchase;
    const today = new Date();
    const paidPayments = Purchase.getPaidPayments(this.props.purchase);

    if (new Date(nextPaymentDueDeadline) <= today && !paidPayments.length) {
      return <ExpiredCopyNoPayments merchantName={merchant.displayName} />;
    }

    if (new Date(nextPaymentDueDeadline) <= today && paidPayments.length) {
      return <ExpiredCopyWithPayments merchantName={merchant.displayName} />;
    }

    if (paidPayments.length > 0) {
      return (
        <ExpiryWarningWithPayments
          nextPaymentDueDeadline={nextPaymentDueDeadline}
          merchantName={merchant.displayName}
        />
      );
    }

    return (
      <ExpiryWarningNoPayments
        nextPaymentDueDeadline={nextPaymentDueDeadline}
        merchantName={merchant.displayName}
      />
    );
  }

  renderDisableOverlay() {
    const { disable, expired } = this.props;

    if (disable || expired) return <DisabledOverlay />;
  }

  renderCollapsableSection() {
    const className = 'collapse navbar-collapse rounded-right';
    return (
      <div className={className} id="organizer-controls-nav">
        {this.renderMenuOptions()}
        {this.renderForm()}
      </div>
    );
  }

  renderControlsLabel() {
    return (
      <div className="controls-label rounded-left">
        <FormattedMessage
          id="organizerControls.title"
          defaultMessage="Organizer Controls"
        />
      </div>
    );
  }

  renderExpirationDate() {
    const { dueDeadline, commitDeadline, expired } = this.props;
    const extraClassname = Purchase.isDateExpired(commitDeadline) ? 'coral-error' : '';

    if (!dueDeadline) return;

    let element = (
      <span className="navbar-text">
        <span className={extraClassname}><Time format={TIME_FORMAT_RELATIVE} time={dueDeadline} /></span>
        <i className="fa fa-fw fa-clock-o" />
      </span>
    );

    if (expired) {
      element = (
        <span className="navbar-text coral-error">
          <span className="coral-error">
            <FormattedMessage
              id="organizerControls.expirationDate.expired"
              defaultMessage="Expired"
            />
          </span>
          <i className="fa fa-fw fa-clock-o" />
        </span>
      );
    }

    return (
      <div className="nav-item expiration-date">
        <ElementWithPopover
          content={this.expirationDateCopy()}
          name="expiration-date-button"
        >
          {element}
        </ElementWithPopover>
      </div>
    );
  }

  renderForm() {
    return (
      <form className="form-inline">
        {this.renderExpirationDate()}
        {this.renderMakePurchaseButton()}
      </form>
    );
  }

  renderMakePurchaseButton() {
    const { purchase } = this.props;
    const className = this.canMakePurchase() ? '' : 'disabled-make-purchase-popover';

    const somePaymentsAuthed = purchase.payments.some((payment) => {
      return Payment.isAuthorized(payment.status) || Payment.isPaid(payment.status);
    });

    const button = (
      <button
        type="button"
        className={`btn btn-primary make-purchase-button ${className}`}
        onClick={this.props.onMakePurchase}
        disabled={!this.canMakePurchase()}
      >
        {somePaymentsAuthed ? (
          <FormattedMessage
            id="organizerControls.sendPayment"
            defaultMessage="Send Payment"
          />
        ) : (
            <FormattedMessage
              id="organizerControls.makePurchase"
              defaultMessage="Make Purchase"
            />
          )}
      </button>
    );

    if (this.canMakePurchase()) {
      return (
        <div className="nav-item pr-0 border-0 pr-4">
          {button}
        </div>
      );
    }

    if (!this.showDifferencePopover()) {
      return (
        <div className={`nav-item pr-0 border-0 pr-4 ${className}`}>
          {button}
        </div>
      );
    }

    return (
      <div className="nav-item pr-0 border-0 pr-4 disabled-make-purchase-popover">
        <ElementWithPopover
          content={this.cantMakePurchaseCopy()}
          name="make-purchase-button"
          actionLink="contribute-link"
          linkAction={this.onCoverDifference}
          constraints={[{ elem: '.organizer-controls', pin: ['right'] }]}
        >
          {button}
        </ElementWithPopover>
      </div>
    );
  }

  renderMenuOptions() {
    return (
      <ul className="navbar-nav mr-auto">
        <li className="nav-item p-relative">
          <a className="nav-link" href="#invite" onClick={this.props.onInvite} >
            <FormattedMessage
              id="organizerControls.menu.invitePeople"
              defaultMessage="Invite People"
            />
          </a>
          {this.renderDisableOverlay()}
        </li>
        <li className="nav-item p-relative">
          <a className="nav-link" href="#edit" onClick={this.onMakeChanges} >
            <FormattedMessage
              id="organizerControls.menu.makeChanges"
              defaultMessage="Make Changes"
            />
          </a>
          {this.renderDisableOverlay()}
        </li>
      </ul>
    );
  }

  renderTogglerButton() {
    return (
      <button
        type="button"
        className="navbar-toggler navbar-toggler-right"
        data-toggle="collapse"
        data-target="#organizer-controls-nav"
      >
        <span className="navbar-toggler-icon" />
      </button>
    );
  }

  render() {
    return (
      <div className="organizer-controls mb-4">
        <nav className="navbar navbar-toggleable-md navbar-light rounded">
          {this.renderTogglerButton()}
          {this.renderControlsLabel()}
          {this.renderCollapsableSection()}
        </nav>
      </div>
    );
  }
}

export default injectIntl(OrganizerControls);



// WEBPACK FOOTER //
// ./src/desktop/components/purchase-dashboard/organizer-controls.js